import React, { useEffect, useState } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, KeyboardAvoidingView, Platform } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { WorkStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { JOB_CATEGORIES } from '../../data/jobCategories';
import { INDIAN_STATES, getDistrictsForState } from '../../data/indianDistricts';
import { useWork } from '../../context/WorkContext';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/work/EmptyState';
import ThemedText from '../../components/common/ThemedText';
import { CustomPicker } from '../../components/common/CustomPicker';
import { DatePicker } from '../../components/common/DatePicker';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { Job } from '../../types/work';

const PROJECT_LEVELS = ['Beginner', 'Medium', 'Expert'];
const DURATIONS = [
  'Less than a week',
  'Less than a month',
  '1-3 months',
  '3-6 months',
  'More than 6 months',
];
const CONTRACT_TYPES = ['Agency Contractors', 'Independent Contractors', 'New Rising Talent'];
const LOCATION_TYPES = ['Remote', 'Hybrid', 'Onsite'];

const SUPPLY_CATEGORIES = [
  'Construction Materials',
  'Steel & Metals',
  'Cement & Concrete',
  'Wood & Timber',
  'Sand & Aggregates',
  'Bricks & Blocks',
  'Electrical Materials',
  'Plumbing Materials',
  'Paint & Coatings',
  'Hardware & Tools',
  'Chemicals & Adhesives',
  'Plastics & Polymers',
  'Glass & Ceramics',
  'Textiles & Fabrics',
  'Packaging Materials',
  'Office Supplies',
  'Food & Beverages',
  'Agricultural Supplies',
  'Industrial Equipment',
  'Machinery Parts',
  'Electronics Components',
  'Safety Equipment',
  'Cleaning Supplies',
  'Other Raw Materials',
];

const SUPPLY_UNITS = [
  'Tons', 'Kg', 'Grams', 'Pounds',
  'Liters', 'Gallons', 'Cubic Meters',
  'Meters', 'Feet', 'Yards',
  'Pieces', 'Units', 'Boxes', 'Dozens', 'Sets',
  'Sq. Meters', 'Sq. Feet',
];

type EditJobNavigationProp = StackNavigationProp<WorkStackParamList, 'EditJob'>;
type EditJobRouteProp = RouteProp<WorkStackParamList, 'EditJob'>;

const EditJobScreen: React.FC = () => {
  const navigation = useNavigation<EditJobNavigationProp>();
  const route = useRoute<EditJobRouteProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const { fetchJobById, updateJob } = useWork();
  const { jobId } = route.params;

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [projectLevel, setProjectLevel] = useState('');
  const [duration, setDuration] = useState('');
  const [contractType, setContractType] = useState('');
  const [locationType, setLocationType] = useState('');
  const [workAddress, setWorkAddress] = useState('');
  const [state, setState] = useState('');
  const [district, setDistrict] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [projectExpiryDate, setProjectExpiryDate] = useState<Date | undefined>();
  const [projectDeadlineDate, setProjectDeadlineDate] = useState<Date | undefined>();
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const [errors, setErrors] = useState({ title: false, category: false, description: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const { keyboardHeight } = useKeyboardHeight();

  // PDF attachment
  const [pdfAttachment, setPdfAttachment] = useState<{
    uri: string;
    name: string;
    size: number;
  } | null>(null);
  const [pdfChanged, setPdfChanged] = useState(false);

  const isContract = job?.type === 'contract';
  const isSupply = (job as any)?.type === 'supply';
  
  // Get districts based on selected state
  const availableDistricts = state ? getDistrictsForState(state) : [];

  const formatDateInput = (value?: string | null) => {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date;
  };

  const toISODateString = (value: Date | undefined) => {
    if (!value) return null;
    return value.toISOString();
  };

  useEffect(() => {
    loadJob();
  }, [jobId]);

  useEffect(() => {
    if (locationType === 'Remote') {
      setWorkAddress('');
    }
  }, [locationType]);

  const loadJob = async () => {
    setLoading(true);
    const jobData = await fetchJobById(jobId);
    setJob(jobData);
    if (jobData) {
      setTitle(jobData.title);
      setCategory(jobData.category);
      setDescription(jobData.description);
      setBudget(jobData.budget ? jobData.budget.toString() : '');
      if (jobData.type === 'contract') {
        setProjectLevel(jobData.projectLevel || '');
        setDuration(jobData.duration || '');
        setContractType(jobData.contractType || '');
        setLocationType(jobData.locationType || '');
        setWorkAddress(jobData.workAddress || '');
        setState((jobData as any).state || '');
        setDistrict((jobData as any).district || '');
        setExperienceYears(
          jobData.experienceYears !== undefined && jobData.experienceYears !== null
            ? jobData.experienceYears.toString()
            : ''
        );
        setProjectExpiryDate(formatDateInput(jobData.projectExpiryDate));
        setProjectDeadlineDate(formatDateInput(jobData.projectDeadlineDate));
        
        // Set existing PDF if available
        if (jobData.contractPdfUrl) {
          setPdfAttachment({
            uri: jobData.contractPdfUrl,
            name: jobData.contractPdfName || 'contract.pdf',
            size: jobData.contractPdfSize || 0,
          });
        }
      } else if ((jobData as any).type === 'supply') {
        setQuantity((jobData as any).quantity ? (jobData as any).quantity.toString() : '');
        setUnit((jobData as any).unit || '');
        setState((jobData as any).state || '');
        setDistrict((jobData as any).district || '');
      } else {
        setProjectLevel('');
        setDuration('');
        setContractType('');
        setLocationType('');
        setWorkAddress('');
        setState('');
        setDistrict('');
        setExperienceYears('');
        setProjectExpiryDate(undefined);
        setProjectDeadlineDate(undefined);
      }
    }
    setLoading(false);
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const handlePickPDF = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets[0]) {
        const file = result.assets[0];
        setPdfAttachment({
          uri: file.uri,
          name: file.name,
          size: file.size || 0,
        });
        setPdfChanged(true);
      }
    } catch (error) {
      console.error('Error picking PDF:', error);
      showErrorToast('Failed to pick PDF file');
    }
  };

  const handleRemovePDF = () => {
    setPdfAttachment(null);
    setPdfChanged(true);
  };

  const handleSaveChanges = async () => {
    setErrors({ title: false, category: false, description: false });

    const newErrors = {
      title: !title.trim(),
      category: !category.trim(),
      description: !description.trim(),
    };

    setErrors(newErrors);

    if (newErrors.title || newErrors.category || newErrors.description) {
      return;
    }

    const payload: Partial<Job> = {
      title,
      category,
      description,
      budget: budget ? parseFloat(budget) : undefined,
    };

    if (isContract) {
      const parsedExperience = experienceYears ? parseInt(experienceYears, 10) : NaN;
      payload.projectLevel = projectLevel || undefined;
      payload.duration = duration || undefined;
      payload.contractType = contractType || undefined;
      payload.locationType = locationType || undefined;
      payload.workAddress = locationType && locationType !== 'Remote' ? (workAddress.trim() || undefined) : undefined;
      (payload as any).state = state || undefined;
      (payload as any).district = district || undefined;
      payload.experienceYears = Number.isNaN(parsedExperience) ? undefined : parsedExperience;
      payload.projectExpiryDate = toISODateString(projectExpiryDate) || undefined;
      payload.projectDeadlineDate = toISODateString(projectDeadlineDate) || undefined;
    } else if (isSupply) {
      (payload as any).quantity = quantity ? parseFloat(quantity) : undefined;
      (payload as any).unit = unit || undefined;
      (payload as any).state = state || undefined;
      (payload as any).district = district || undefined;
    }

    setIsSubmitting(true);
    try {
      // Determine PDF parameter
      let pdfParam: { uri: string; name: string; size: number } | null | undefined;
      if (isContract && pdfChanged) {
        pdfParam = pdfAttachment;
      }
      
      await updateJob(jobId, payload, pdfParam);
      showSuccessToast('Job updated successfully!');
      navigation.goBack();
    } catch (error) {
      showErrorToast('Failed to update job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  if (!job) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.headerPlaceholder}>
          <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
            <Ionicons name="close" size={28} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Edit Job</ThemedText>
          <View style={styles.headerButton} />
        </View>
        <EmptyState icon="alert-circle-outline" message="Job not found" />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View
        style={[styles.header, { borderBottomColor: theme.border }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          if (height !== headerHeight) {
            setHeaderHeight(height);
          }
        }}
      >
        <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          {isSupply ? 'Edit Supplies Post' : isContract ? 'Edit Contract Post' : 'Edit Job'}
        </ThemedText>
        <View style={styles.headerButton} />
      </View>

      <KeyboardAvoidingView 
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
      >
        <ScrollView 
          style={styles.content} 
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
        <View style={styles.form}>
          <View style={styles.sectionHeader}>
            <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Basic Information</ThemedText>
            <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Update the essentials for this post</ThemedText>
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Job Title *</ThemedText>
            <TextInput
              style={[
                styles.input,
                { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border },
                errors.title && { borderWidth: 2, borderColor: theme.error },
              ]}
              placeholder="Enter job title"
              value={title}
              onChangeText={(text) => {
                setTitle(text);
                if (errors.title) setErrors({ ...errors, title: false });
              }}
              placeholderTextColor={theme.textSecondary}
            />
            {errors.title && (
              <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
            )}
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Category *</ThemedText>
            <CustomPicker
              selectedValue={category || undefined}
              onValueChange={(value) => {
                setCategory(value || '');
                if (errors.category) setErrors({ ...errors, category: false });
              }}
              items={[
                { label: 'Select a category', value: undefined },
                ...(isSupply ? SUPPLY_CATEGORIES : JOB_CATEGORIES).map(cat => ({ label: cat, value: cat }))
              ]}
              placeholder="Select a category"
              searchable={true}
              searchPlaceholder="Search categories..."
            />
            {errors.category && (
              <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
            )}
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Description *</ThemedText>
            <TextInput
              style={[
                styles.input,
                styles.textArea,
                { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border },
                errors.description && { borderWidth: 2, borderColor: theme.error },
              ]}
              placeholder="Describe the job requirements..."
              value={description}
              onChangeText={(text) => {
                setDescription(text);
                if (errors.description) setErrors({ ...errors, description: false });
              }}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              placeholderTextColor={theme.textSecondary}
            />
            {errors.description && (
              <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
            )}
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Budget (Optional)</ThemedText>
            <TextInput
              style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border }]}
              placeholder="e.g., 5000"
              value={budget}
              onChangeText={setBudget}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          {isSupply && (
            <>
              <View style={styles.sectionHeader}>
                <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Supply Details</ThemedText>
                <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Specify quantity and unit of measurement</ThemedText>
              </View>

              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>Quantity *</ThemedText>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border }]}
                  placeholder="e.g., 10, 100, 500"
                  value={quantity}
                  onChangeText={setQuantity}
                  keyboardType="decimal-pad"
                  placeholderTextColor={theme.textSecondary}
                />
              </View>

              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>Unit *</ThemedText>
                <CustomPicker
                  selectedValue={unit || undefined}
                  onValueChange={(value) => setUnit(value || '')}
                  items={[
                    { label: 'Select unit', value: undefined },
                    ...SUPPLY_UNITS.map(u => ({ label: u, value: u })),
                    { label: 'Custom', value: 'Custom' }
                  ]}
                  placeholder="Select unit"
                  searchable={true}
                  searchPlaceholder="Search units..."
                />
              </View>

              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>State</ThemedText>
                <CustomPicker
                  selectedValue={state || undefined}
                  onValueChange={(value) => {
                    setState(value || '');
                    // Reset district when state changes
                    if (value !== state) {
                      setDistrict('');
                    }
                  }}
                  items={[
                    { label: 'Select State', value: undefined },
                    ...INDIAN_STATES.map(s => ({ label: s, value: s }))
                  ]}
                  placeholder="Select State"
                  searchable={true}
                  searchPlaceholder="Search states..."
                />
              </View>

              {state && (
                <View style={styles.formGroup}>
                  <ThemedText style={[styles.label, { color: theme.text }]}>District</ThemedText>
                  <CustomPicker
                    selectedValue={district || undefined}
                    onValueChange={(value) => setDistrict(value || '')}
                    items={[
                      { label: 'Select District', value: undefined },
                      ...availableDistricts.map(d => ({ label: d, value: d }))
                    ]}
                    placeholder="Select District"
                    searchable={true}
                    searchPlaceholder="Search districts..."
                  />
                </View>
              )}
            </>
          )}

          {isContract && (
            <>
              <View style={styles.sectionHeader}>
                <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Project Details</ThemedText>
                <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Help contractors understand scope and expectations</ThemedText>
              </View>

              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>Project Level</ThemedText>
                <CustomPicker
                  selectedValue={projectLevel || undefined}
                  onValueChange={(value) => setProjectLevel(value || '')}
                  items={[
                    { label: 'Select project level', value: undefined },
                    ...PROJECT_LEVELS.map(level => ({ label: level, value: level }))
                  ]}
                  placeholder="Select project level"
                />
              </View>

              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>Duration</ThemedText>
                <CustomPicker
                  selectedValue={duration || undefined}
                  onValueChange={(value) => setDuration(value || '')}
                  items={[
                    { label: 'Select duration', value: undefined },
                    ...DURATIONS.map(dur => ({ label: dur, value: dur }))
                  ]}
                  placeholder="Select duration"
                />
              </View>

              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>Contract Type</ThemedText>
                <CustomPicker
                  selectedValue={contractType || undefined}
                  onValueChange={(value) => setContractType(value || '')}
                  items={[
                    { label: 'Select contract type', value: undefined },
                    ...CONTRACT_TYPES.map(type => ({ label: type, value: type }))
                  ]}
                  placeholder="Select contract type"
                />
              </View>

              <View style={styles.sectionHeader}>
                <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Location & Requirements</ThemedText>
                <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Set expectations for where and how the work happens</ThemedText>
              </View>

              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>Location Type</ThemedText>
                <CustomPicker
                  selectedValue={locationType || undefined}
                  onValueChange={(value) => setLocationType(value || '')}
                  items={[
                    { label: 'Select location type', value: undefined },
                    ...LOCATION_TYPES.map(type => ({ label: type, value: type }))
                  ]}
                  placeholder="Select location type"
                />
              </View>

              {locationType && locationType !== 'Remote' && (
                <>
                  <View style={styles.formGroup}>
                    <ThemedText style={[styles.label, { color: theme.text }]}>State</ThemedText>
                    <CustomPicker
                      selectedValue={state || undefined}
                      onValueChange={(value) => {
                        setState(value || '');
                        // Reset district when state changes
                        if (value !== state) {
                          setDistrict('');
                        }
                      }}
                      items={[
                        { label: 'Select State', value: undefined },
                        ...INDIAN_STATES.map(s => ({ label: s, value: s }))
                      ]}
                      placeholder="Select State"
                      searchable={true}
                      searchPlaceholder="Search states..."
                    />
                  </View>

                  {state && (
                    <View style={styles.formGroup}>
                      <ThemedText style={[styles.label, { color: theme.text }]}>District</ThemedText>
                      <CustomPicker
                        selectedValue={district || undefined}
                        onValueChange={(value) => setDistrict(value || '')}
                        items={[
                          { label: 'Select District', value: undefined },
                          ...availableDistricts.map(d => ({ label: d, value: d }))
                        ]}
                        placeholder="Select District"
                        searchable={true}
                        searchPlaceholder="Search districts..."
                      />
                    </View>
                  )}

                  <View style={styles.formGroup}>
                    <ThemedText style={[styles.label, { color: theme.text }]}>Work Address</ThemedText>
                    <TextInput
                      style={[styles.input, styles.textArea, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border }]}
                      placeholder="Enter the onsite or hybrid work location"
                      value={workAddress}
                      onChangeText={setWorkAddress}
                      multiline
                      numberOfLines={3}
                      textAlignVertical="top"
                      placeholderTextColor={theme.textSecondary}
                    />
                  </View>
                </>
              )}

              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>Years of Experience Preferred</ThemedText>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border }]}
                  placeholder="e.g., 5"
                  value={experienceYears}
                  onChangeText={setExperienceYears}
                  keyboardType="numeric"
                  placeholderTextColor={theme.textSecondary}
                />
              </View>

              <View style={styles.sectionHeader}>
                <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Project Timeline</ThemedText>
                <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Keep applicants informed about key dates</ThemedText>
              </View>

              <DatePicker
                value={projectExpiryDate}
                onChange={setProjectExpiryDate}
                label="Project Expiry Date (Optional)"
                helperText="When this listing should expire"
                minimumDate={new Date()}
              />

              <DatePicker
                value={projectDeadlineDate}
                onChange={setProjectDeadlineDate}
                label="Project Deadline Date (Optional)"
                helperText="When the project needs to be completed"
                minimumDate={new Date()}
              />

              {/* PDF Attachment Section for Contracts */}
              <View style={styles.sectionHeader}>
                <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Contract Document</ThemedText>
                <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Attach PDF contract (optional)</ThemedText>
              </View>

              <View style={styles.formGroup}>
                {!pdfAttachment ? (
                  <TouchableOpacity
                    style={[styles.pdfPickerButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
                    onPress={handlePickPDF}
                  >
                    <Ionicons name="document-attach-outline" size={32} color={theme.accent} />
                    <ThemedText style={[styles.pdfPickerText, { color: theme.text }]}>Upload PDF Document</ThemedText>
                    <ThemedText style={[styles.pdfPickerSubtext, { color: theme.textSecondary }]}>Max size: 10MB</ThemedText>
                  </TouchableOpacity>
                ) : (
                  <View style={[styles.pdfAttachmentCard, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}>
                    <View style={styles.pdfAttachmentInfo}>
                      <Ionicons name="document-text" size={40} color={theme.accent} />
                      <View style={styles.pdfAttachmentDetails}>
                        <ThemedText style={[styles.pdfFileName, { color: theme.text }]} numberOfLines={1}>
                          {pdfAttachment.name}
                        </ThemedText>
                        <ThemedText style={[styles.pdfFileSize, { color: theme.textSecondary }]}>
                          {(pdfAttachment.size / 1024 / 1024).toFixed(2)} MB
                        </ThemedText>
                      </View>
                    </View>
                    <TouchableOpacity onPress={handleRemovePDF} style={styles.pdfRemoveButton}>
                      <Ionicons name="close-circle" size={28} color={theme.error} />
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            </>
          )}

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity style={[styles.cancelButton, { borderColor: theme.border }]} onPress={handleCancel} disabled={isSubmitting}>
              <ThemedText style={[styles.cancelButtonText, { color: theme.textSecondary }]}>Cancel</ThemedText>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.postButton, { backgroundColor: theme.accent, opacity: isSubmitting ? 0.7 : 1 }]} 
              onPress={handleSaveChanges}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator color={theme.textInverse} />
              ) : (
                <ThemedText style={[styles.postButtonText, { color: theme.textInverse }]}>Save Changes</ThemedText>
              )}
            </TouchableOpacity>
          </View>
          <FormFooterSpacer
            headerHeight={headerHeight}
            bottomInset={insets.bottom}
            keyboardHeight={keyboardHeight}
          />
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerPlaceholder: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 120,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  form: {
    padding: 16,
  },
  sectionHeader: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 13,
  },
  formGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    borderWidth: 1,
  },
  pickerInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
  },
  pickerText: {
    fontSize: 16,
  },
  textArea: {
    minHeight: 120,
    paddingTop: 12,
  },
  errorText: {
    fontSize: 12,
    marginTop: 4,
  },
  quickActionsRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  quickAction: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
  },
  quickActionText: {
    fontSize: 13,
    fontWeight: '600',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  postButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  postButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  pdfPickerButton: {
    padding: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pdfPickerText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
  },
  pdfPickerSubtext: {
    fontSize: 14,
    marginTop: 4,
  },
  pdfAttachmentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  pdfAttachmentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  pdfAttachmentDetails: {
    flex: 1,
    marginLeft: 12,
  },
  pdfFileName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  pdfFileSize: {
    fontSize: 14,
  },
  pdfRemoveButton: {
    padding: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  categoryOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  categoryOptionText: {
    fontSize: 16,
    fontWeight: '500',
  },
  datePickerDone: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  datePickerDoneText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default EditJobScreen;
